import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { LuChevronDown } from "react-icons/lu";
import { IconChevronRight } from "@tabler/icons-react";

export type SubmenuOption<T> = {
  key: string;
  label: string;
  value?: T;
  disabled?: boolean;
  // When provided, a right-side panel will render this content instead of selecting immediately
  renderSubmenu?: (helpers: {
    close: () => void;
    select: (value: T) => void;
  }) => React.ReactNode;
};

export type SubmenuDropdownProps<T> = {
  options: SubmenuOption<T>[];
  selected?: T | null;
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  /** Optional icon component to display next to the label */
  labelIcon?: React.ComponentType<{ className?: string }> | null;
  /** Position of the label relative to the selected option. Defaults to 'left' */
  labelPosition?: "left" | "right";
  placeholder?: string;
  /** Position of the dropdown. Defaults to 'start' */
  dropdownAlign?: "start" | "end";
  /** Custom trigger node; used as child of PopoverTrigger when provided */
  customTrigger?: React.ReactNode;
  /** Hide selected option text when its key is included */
  hideSelectedWhenKeyIn?: string[];
  /** Notifies when a submenu (by key) opens or closes */
  onSubmenuOpenChange?: (key: string, isOpen: boolean) => void;
  /** Notifies when the dropdown opens or closes */
  onOpenChange?: (isOpen: boolean) => void;
};

function SubmenuDropdown<T>({
  options,
  selected,
  onChange,
  className,
  disabled,
  label,
  labelIcon,
  labelPosition = "left",
  placeholder = "Select",
  dropdownAlign = "start",
  customTrigger,
  hideSelectedWhenKeyIn,
  onSubmenuOpenChange,
  onOpenChange,
}: SubmenuDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [activeSubmenuKey, setActiveSubmenuKey] = useState<string | null>(null);

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === selected);
  }, [options, selected]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setActiveSubmenuKey(null);
    onOpenChange?.(isOpen);
  };

  const renderIcon = () => {
    if (!labelIcon) return null;
    const LabelIconComponent = labelIcon;
    return (
      <LabelIconComponent className="h-[14px] w-[14px] text-brand-primary-400 shrink-0" />
    );
  };

  const renderLabel = () => {
    if (!label) return null;
    return (
      <div className="flex items-center gap-2 shrink-0">
        {typeof label === "string" ? (
          <span className="overflow-hidden text-brand-primary-600 text-ellipsis text-[11px] font-normal leading-none tracking-[0.24px]">
            {label}
          </span>
        ) : (
          label
        )}
      </div>
    );
  };

  const shouldHideSelected =
    !!selectedOption && !!hideSelectedWhenKeyIn?.includes(selectedOption.key);

  const summaryLabel = selectedOption?.label || placeholder;

  const renderSelected = () => {
    if (shouldHideSelected) return null;
    return (
      <span
        className={cn(
          "flex-1 line-clamp-1 truncate text-brand-primary-800 text-[11px] leading-none tracking-[0.24px] capitalize",
          "font-medium",
        )}
      >
        {summaryLabel}
      </span>
    );
  };

  const renderTrigger = () => (
    <div className="flex items-center gap-[6px] overflow-hidden">
      {renderIcon()}
      {labelPosition === "left" ? (
        <>
          {renderLabel()}
          {renderSelected()}
        </>
      ) : (
        <>
          {renderSelected()}
          {renderLabel()}
        </>
      )}
    </div>
  );

  const getTriggerClassName = () => {
    const baseClasses =
      "flex items-center justify-between overflow-hidden whitespace-nowrap bg-white hover-gray text-[11px] focus:outline-none focus:ring-0";
    const outlineClasses =
      "h-[32px] px-3 py-[6px] rounded-[8px]  squircle squircle-radius-[8px] squircle-outline-color-border-gray squircle-outline-[1px] shadow-sm";
    return cn(baseClasses, outlineClasses, className);
  };

  const simpleSelect = (value: T) => {
    onChange(value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger
        disabled={!!disabled}
        className={customTrigger ? undefined : getTriggerClassName()}
        asChild={!!customTrigger}
      >
        {customTrigger ? (
          customTrigger
        ) : (
          <div className="flex items-center overflow-hidden">
            {renderTrigger()}
            {!disabled && (
              <LuChevronDown className="ml-[6px] h-[14px] w-[14px] text-brand-primary-400 shrink-0" />
            )}
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={dropdownAlign}
        className={cn(
          "max-w-[760px] p-1 border-0 shadow-none squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white w-auto",
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        portalled={true}
      >
        {/* Left list - each item with submenu is its own popover trigger opening to the right */}
        <div className="min-w-[180px] max-w-[220px]">
          {options.map((item) => {
            const isSelected = item.value !== undefined && item.value === selected;
            const hasSub = !!item.renderSubmenu;

            if (hasSub) {
              return (
                <Popover
                  key={item.key}
                  open={activeSubmenuKey === item.key}
                  onOpenChange={(isOpen) => {
                    if (item.disabled) return;
                    setActiveSubmenuKey(isOpen ? item.key : null);
                    onSubmenuOpenChange?.(item.key, isOpen);
                  }}
                >
                  <PopoverTrigger asChild disabled={item.disabled}>
                    <div
                      className={cn(
                        "group w-full px-[8px] py-[4px] rounded-[8px]",
                        item.disabled
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer",
                        !item.disabled &&
                          (isSelected ? "bg-selected-gray" : "hover:bg-hover-gray"),
                      )}
                    >
                      <div className="flex justify-between items-center gap-2">
                        <div className="w-full flex items-center gap-2">
                          <span
                            className={cn(
                              "text-[12px] font-normal leading-[20px]",
                              item.disabled
                                ? "text-brand-primary-400"
                                : "text-brand-primary-800",
                            )}
                          >
                            {item.label}
                          </span>
                        </div>
                        {!item.disabled && (
                          <IconChevronRight className="h-[16px] w-[16px] text-brand-primary-400" />
                        )}
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    side="right"
                    sideOffset={10}
                    align="start"
                    alignOffset={-88}
                    className={cn("p-0 w-auto shadow-none border-none rounded-[12px]")}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    portalled={true}
                  >
                    <div className="rounded-[12px]">
                      {item.renderSubmenu?.({
                        close: () => {
                          setActiveSubmenuKey(null);
                          setOpen(false);
                        },
                        select: (value: T) => {
                          simpleSelect(value);
                          setActiveSubmenuKey(null);
                        },
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }

            return (
              <div
                key={item.key}
                onClick={() => {
                  if (item.disabled) return;
                  if (item.value !== undefined) simpleSelect(item.value);
                  setActiveSubmenuKey(null);
                }}
                className={cn(
                  "group w-full px-[8px] py-[4px] rounded-[8px]",
                  item.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                  !item.disabled &&
                    (isSelected ? "bg-selected-gray" : "hover:bg-hover-gray"),
                )}
              >
                <div className="flex justify-between items-center gap-2">
                  <div className="w-full flex items-center gap-2">
                    <span
                      className={cn(
                        "text-[12px] font-normal leading-[20px]",
                        item.disabled
                          ? "text-brand-primary-400"
                          : "text-brand-primary-800",
                      )}
                    >
                      {item.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SubmenuDropdown;
